/**
 * useOperatorMenu — shared store wiring for floating operator menus.
 *
 * Every operator menu (Delete, Merge, Relax, Gravity, ObjectDeleteConfirm)
 * reads the same trio of store values — its own `open` flag, the shared
 * `operatorAnchorPoint`, and a setter for each — and closes by calling
 * `setOpen(false) + setAnchorPoint(null)`. This hook is the canonical
 * binding for that trio.
 *
 * If a menu needs to clear additional local or store state on close, wrap
 * the returned `close` in a `useCallback` that calls the extras first.
 */

import { useCallback } from "react";
import { useStore } from "../../app/store.ts";

export type OperatorMenuKey =
  | "delete"
  | "merge"
  | "relax"
  | "gravity"
  | "smooth"
  | "objectDelete"
  | "renderMethodPie"
  | "snapPie"
  | "viewPie"
  | "extrudePie";

interface OperatorMenuSliceKeys {
  openKey:
    | "deleteMenuOpen"
    | "mergeMenuOpen"
    | "relaxMenuOpen"
    | "gravityMenuOpen"
    | "smoothMenuOpen"
    | "objectDeleteConfirmOpen"
    | "renderMethodPieOpen"
    | "snapPieOpen"
    | "viewPieOpen"
    | "extrudePieOpen";
  setOpenKey:
    | "setDeleteMenuOpen"
    | "setMergeMenuOpen"
    | "setRelaxMenuOpen"
    | "setGravityMenuOpen"
    | "setSmoothMenuOpen"
    | "setObjectDeleteConfirmOpen"
    | "setRenderMethodPieOpen"
    | "setSnapPieOpen"
    | "setViewPieOpen"
    | "setExtrudePieOpen";
}

const SLICE_KEYS: Record<OperatorMenuKey, OperatorMenuSliceKeys> = {
  delete: { openKey: "deleteMenuOpen", setOpenKey: "setDeleteMenuOpen" },
  merge: { openKey: "mergeMenuOpen", setOpenKey: "setMergeMenuOpen" },
  relax: { openKey: "relaxMenuOpen", setOpenKey: "setRelaxMenuOpen" },
  gravity: { openKey: "gravityMenuOpen", setOpenKey: "setGravityMenuOpen" },
  smooth: { openKey: "smoothMenuOpen", setOpenKey: "setSmoothMenuOpen" },
  objectDelete: {
    openKey: "objectDeleteConfirmOpen",
    setOpenKey: "setObjectDeleteConfirmOpen",
  },
  renderMethodPie: {
    openKey: "renderMethodPieOpen",
    setOpenKey: "setRenderMethodPieOpen",
  },
  snapPie: { openKey: "snapPieOpen", setOpenKey: "setSnapPieOpen" },
  viewPie: { openKey: "viewPieOpen", setOpenKey: "setViewPieOpen" },
  extrudePie: { openKey: "extrudePieOpen", setOpenKey: "setExtrudePieOpen" },
};

export interface OperatorMenuBinding {
  open: boolean;
  setOpen: (open: boolean) => void;
  anchorPoint: { x: number; y: number } | null;
  /** Close the menu and clear the shared anchor point. */
  close: () => void;
}

export function useOperatorMenu(key: OperatorMenuKey): OperatorMenuBinding {
  const { openKey, setOpenKey } = SLICE_KEYS[key];
  const open = useStore((s) => s[openKey] as boolean);
  const setOpen = useStore((s) => s[setOpenKey] as (open: boolean) => void);
  const anchorPoint = useStore((s) => s.operatorAnchorPoint);
  const setAnchorPoint = useStore((s) => s.setOperatorAnchorPoint);

  const close = useCallback(() => {
    setOpen(false);
    setAnchorPoint(null);
  }, [setOpen, setAnchorPoint]);

  return { open, setOpen, anchorPoint, close };
}
